/**
 * ForgotPasswordPage — Screen 3 of the auth flow (design: 05-auth.md).
 *
 * Route: /forgot-password
 *
 * The confirmation message shown after submission is identical whether or not
 * the email is registered (TC-008 / user-enumeration prevention).
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthCard } from './AuthCard';
import { EmailField } from './EmailField';
import { forgotPassword } from '../../api/auth';

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

type PageState = 'form' | 'sent';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [state, setState] = useState<PageState>('form');
  const [submitting, setSubmitting] = useState(false);

  function validateEmail(): boolean {
    if (!email.trim() || !isValidEmail(email)) {
      setEmailError('Please enter a valid email address.');
      return false;
    }
    setEmailError('');
    return true;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!validateEmail()) {
      const emailInput = document.getElementById('forgot-email');
      if (emailInput instanceof HTMLInputElement) emailInput.focus();
      return;
    }

    setSubmitting(true);
    try {
      await forgotPassword(email);
    } catch {
      // Even on error, show the same confirmation (TC-008: no enumeration).
      // A real error here would be a server fault, not a "not found".
    } finally {
      setSubmitting(false);
      // Always transition to "sent" state regardless of outcome.
      setState('sent');
    }
  }

  if (state === 'sent') {
    return (
      <AuthCard>
        {/* Screen 3b: Forgot Password Confirmation */}
        <h1 className="text-lg font-bold text-gray-900 mb-3">Check your email.</h1>
        <p className="text-sm text-gray-600 mb-6">
          If an account exists for that address, we&apos;ve sent a reset link. The link
          expires in 1 hour.
        </p>
        <Link
          to="/login"
          className="text-sm text-brand-primary hover:underline flex items-center gap-1"
        >
          &#8592; Back to log in
        </Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard>
      {/* Screen 3: Forgot Password form */}
      <h1 className="text-lg font-bold text-gray-900 mb-2">Forgot your password?</h1>
      <p className="text-sm text-gray-500 mb-5">
        Enter your email and we&apos;ll send a reset link if an account exists.
      </p>

      <form onSubmit={handleSubmit} noValidate>
        <EmailField
          id="forgot-email"
          label="Email"
          value={email}
          onChange={setEmail}
          onBlur={validateEmail}
          error={emailError}
          autoComplete="email"
        />

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-brand-primary text-white font-semibold py-2.5 min-h-[44px] rounded-lg hover:bg-brand-primary-dk focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 motion-safe:transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Sending…' : 'Send reset link'}
        </button>
      </form>

      <div className="mt-4 text-center">
        <Link
          to="/login"
          className="text-sm text-brand-primary hover:underline flex items-center justify-center gap-1"
        >
          &#8592; Back to log in
        </Link>
      </div>
    </AuthCard>
  );
}
